/**
 * P2-06g · Presentational testimonial card + star rating + avatar monogram.
 *
 * No "use client" and no server-only imports, so it renders in both the RSC
 * grid and the client carousel. It takes a resolved view (avatar already a URL,
 * or null → initials monogram); the block does the media resolution.
 */
import { StoreImage } from "~/components/ui/store-image";
import { cn } from "~/lib/utils";

export interface TestimonialView {
  quote: string;
  author: string;
  role: string | null;
  avatarUrl: string | null;
  rating: number | null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

function StarRating({ rating }: { rating: number }) {
  return (
    <span
      role="img"
      aria-label={`${rating} out of 5`}
      className="inline-flex items-center gap-0.5 text-primary"
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} viewBox="0 0 20 20" aria-hidden className="size-4">
          <path
            d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L10 14.9l-5.2 2.62.99-5.8-4.21-4.1 5.82-.85L10 1.5z"
            className={cn(i <= rating ? "fill-current" : "fill-none")}
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </span>
  );
}

export function TestimonialCard({ item }: { item: TestimonialView }) {
  return (
    <figure className="flex h-full flex-col gap-4 rounded-[var(--radius-lg)] border border-border bg-surface p-6">
      {item.rating !== null && <StarRating rating={item.rating} />}
      <blockquote className="text-base leading-relaxed text-fg">
        &ldquo;{item.quote}&rdquo;
      </blockquote>
      <figcaption className="mt-auto flex items-center gap-3">
        {item.avatarUrl ? (
          <span className="relative size-11 shrink-0 overflow-hidden rounded-full bg-surface-2">
            <StoreImage
              src={item.avatarUrl}
              alt=""
              fill
              preset="thumb"
              className="object-cover"
            />
          </span>
        ) : (
          <span
            aria-hidden
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-surface-2 text-sm font-semibold text-fg"
          >
            {initials(item.author)}
          </span>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-fg">
            {item.author}
          </div>
          {item.role && (
            <div className="truncate text-sm text-fg-muted">{item.role}</div>
          )}
        </div>
      </figcaption>
    </figure>
  );
}
